
// CaptionUploadModal.jsx
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Swal from 'sweetalert2';
import {
    Box,
    Modal,
    Typography,
    IconButton,
    Button,
    Divider,
    CircularProgress,
    Link,
    Chip,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';


const CaptionUploadModal = ({
    isOpen,
    onClose,
    onUpload,
    existingCaptionUrl,
    buttonColor,
    showMsgSuccessToast,
    showErrorToast,
}) => {
console.log('existingCaptionUrl', existingCaptionUrl)

    const [captionFile, setCaptionFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const safeButtonColor = buttonColor || '#000'

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: acceptedFiles => {
            setDragOver(false);
            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                if (file.name.endsWith('.srt') || file.name.endsWith('.vtt')) {
                    setCaptionFile(file);
                } else {
                    showErrorToast('Please select a .srt or .vtt file.');
                }
            }
        },
        onDragEnter: () => setDragOver(true),
        onDragLeave: () => setDragOver(false),
        accept: {
            'text/plain': ['.srt', '.vtt'],
            'text/vtt': ['.vtt']
        },
        multiple: false,
    });

    const convertSrtToVtt = srtContent => {
        srtContent = srtContent.replace(/^\uFEFF/, '');
        let vttContent = srtContent
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\n+/g, '\n')
            .trim();

        vttContent = 'WEBVTT\n\n' + vttContent;
        vttContent = vttContent.replace(
            /(\d{2}:\d{2}:\d{2}),(\d{3})/g,
            '$1.$2'
        );
        vttContent = vttContent.replace(/^\d+\s*$/gm, '');

        return vttContent
            .split('\n')
            .filter(line => line.trim() !== '')
            .join('\n');
    };

    const handleUpload = async () => {
        if (!captionFile && !existingCaptionUrl) {
            showErrorToast('Please select a caption file.');
            return;
        }

        // If there's an existing caption but no new file selected, just close the modal
        if (existingCaptionUrl && !captionFile) {
            onClose(); // Just close, don't call onUpload
            return;
        }

        setUploading(true);
        try {
            await onUpload(captionFile, convertSrtToVtt);
            setCaptionFile(null);
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteCaption = () => {
        Swal.fire({
            title: 'Delete Caption?',
            text: 'Are you sure you want to remove this caption file?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: safeButtonColor,
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        }).then(async (result) => {
            if (result.isConfirmed) {
                setUploading(true);
                try {
                    // Pass null to indicate deletion
                    await onUpload(null, null);
                    showMsgSuccessToast('Caption file deleted successfully!');
                    onClose();
                } catch (err) {
                    console.error(err);
                } finally {
                    setUploading(false);
                }
            }
        });
    };

    const getFileSize = (size) => {
        if (size < 1024) return size + ' bytes';
        else if (size < 1048576) return (size / 1024).toFixed(1) + ' KB';
        else return (size / 1048576).toFixed(1) + ' MB';
    };

    const getFileType = (filename) => {
        if (filename.endsWith('.srt')) return 'SRT';
        if (filename.endsWith('.vtt')) return 'VTT';
        return 'Caption';
    };

    return (
        <Modal
            open={isOpen}
            onClose={uploading ? undefined : onClose}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                }
            }}
        >
            <Box
                sx={{
                    width: { xs: '90%', sm: 540, md: 580 },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: '20px',
                    boxShadow: '0 24px 72px rgba(0, 0, 0, 0.3)',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    border: '1px solid',
                    borderColor: alpha(safeButtonColor ? safeButtonColor : '#000', 0.2),
                    '&:focus': { outline: 'none' },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2.5,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DescriptionIcon sx={{
                            color: safeButtonColor,
                            fontSize: 30,
                            backgroundColor: alpha(safeButtonColor, 0.1),
                            p: 1,
                            borderRadius: '10px',
                        }} />
                        <Box>
                            <Typography variant="h5" sx={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                lineHeight: 1.2,
                            }}>
                                Caption Upload
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: '#666',
                                fontSize: '0.8rem',
                            }}>
                                Add subtitles to your video
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={uploading ? undefined : onClose}
                        disabled={uploading}
                        sx={{
                            color: '#6c757d',
                            backgroundColor: '#f8f9fa',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: alpha(safeButtonColor, 0.1),
                                color: safeButtonColor,
                                transform: 'rotate(90deg)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider sx={{
                    mb: 3,
                    borderColor: alpha('#000', 0.1),
                    borderWidth: 1,
                }} />

                {/* Existing Caption Section */}
                {existingCaptionUrl && (
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: '16px',
                            backgroundColor: alpha(safeButtonColor, 0.03),
                            mb: 3.5,
                            border: `1px solid ${alpha(safeButtonColor, 0.15)}`,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                        }}
                    >
                        {/* <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start', 
              mb: 2,
            }}>
              <Box>
                <Typography variant="subtitle1" sx={{ 
                  fontWeight: '700', 
                  color: '#1a1a1a',
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <CheckCircleIcon sx={{ 
                    color: safeButtonColor, 
                    fontSize: 20,
                  }} />
                  Current Caption
                </Typography>
                <Chip
                  label={getFileType(existingCaptionUrl.split('/').pop())}
                  size="small"
                  sx={{
                    backgroundColor: alpha(safeButtonColor, 0.1),
                    color: safeButtonColor,
                    fontWeight: '600',
                    fontSize: '0.75rem',
                    height: '24px',
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleDeleteCaption}
                disabled={uploading}
                sx={{
                  color: '#dc3545',
                  backgroundColor: alpha('#dc3545', 0.1),
                  '&:hover': {
                    backgroundColor: alpha('#dc3545', 0.2),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box> */}

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '10px',
                                backgroundColor: '#fff',
                                border: '1px solid #e9ecef',
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                            }}>
                                <Typography variant="body2" sx={{
                                    color: '#495057',
                                    fontWeight: '500',
                                    fontSize: '0.9rem',
                                }}>
                                    File Name:
                                </Typography>

                            </Box>

                            <Link
                                href={existingCaptionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'block',
                                    fontFamily: 'monospace',
                                    fontSize: '0.95rem',
                                    color: '#1a1a1a',
                                    fontWeight: '600',
                                    textDecoration: 'none',
                                    p: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px dashed #dee2e6',
                                    wordBreak: 'break-all',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: alpha(safeButtonColor, 0.05),
                                        borderColor: safeButtonColor,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DescriptionIcon sx={{
                                        color: safeButtonColor,
                                        fontSize: 18,
                                    }} />
                                    {existingCaptionUrl.split('/').pop()}
                                </Box>
                            </Link>


                        </Box>
                    </Box>
                )}

                {/* Dropzone Section */}
                <Box
                    {...getRootProps()}
                    sx={{
                        border: `2px dashed ${dragOver ? safeButtonColor : '#ced4da'}`,
                        borderRadius: '18px',
                        p: { xs: 3, sm: 4 },
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backgroundColor: captionFile ? alpha(safeButtonColor, 0.04) : '#f8f9fa',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                            borderColor: safeButtonColor,
                            backgroundColor: alpha(safeButtonColor, 0.06),
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha(safeButtonColor, 0.15)}`,
                        },
                        ...(dragOver && {
                            backgroundColor: alpha(safeButtonColor, 0.1),
                            transform: 'scale(1.01)',
                        }),
                    }}
                >
                    <input {...getInputProps()} />

                    {/* Background Pattern */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `radial-gradient(circle at 30% 30%, ${alpha(safeButtonColor, 0.04)} 0%, transparent 50%)`,
                    }} />

                    <CloudUploadIcon sx={{
                        fontSize: 64,
                        color: captionFile ? safeButtonColor : '#adb5bd',
                        mb: 2.5,
                        transition: 'all 0.3s',
                        ...(dragOver && {
                            color: safeButtonColor,
                            transform: 'translateY(-5px)',
                        }),
                    }} />

                    {captionFile ? (
                        <Box>
                            <Typography variant="h6" sx={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                mb: 1,
                            }}>
                                {captionFile.name}
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                            }}>
                                <Chip
                                    label={getFileType(captionFile.name)}
                                    size="small"
                                    sx={{
                                        backgroundColor: alpha(safeButtonColor, 0.1),
                                        color: safeButtonColor,
                                        fontWeight: '600',
                                        fontSize: '0.8rem',
                                    }}
                                />
                                <Typography variant="body2" sx={{
                                    color: '#6c757d',
                                    fontSize: '0.85rem',
                                }}>
                                    {getFileSize(captionFile.size)}
                                </Typography>
                            </Box>

                            <Typography variant="caption" sx={{
                                display: 'block',
                                color: '#868e96',
                                fontSize: '0.85rem',
                            }}>
                                Ready to upload • Click or drag to replace
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="h6" sx={{
                                fontWeight: '700',
                                color: '#343a40',
                                mb: 1.5,
                            }}>
                                {existingCaptionUrl ? 'Upload New Caption' : 'Drop your caption file'}
                            </Typography>

                            <Typography variant="body2" sx={{
                                color: '#6c757d',
                                fontSize: '0.95rem',
                                mb: 3,
                                maxWidth: '400px',
                                mx: 'auto',
                            }}>
                                Drag & drop your .srt or .vtt file here, or click to browse
                            </Typography>

                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: safeButtonColor,
                                    color: '#fff',
                                    borderRadius: '12px',
                                    px: 4,
                                    py: 1.25,
                                    textTransform: 'none',
                                    fontWeight: '700',
                                    fontSize: '0.95rem',
                                    boxShadow: `0 4px 12px ${alpha(safeButtonColor, 0.3)}`,
                                    '&:hover': {
                                        backgroundColor: safeButtonColor,
                                        boxShadow: `0 6px 20px ${alpha(safeButtonColor, 0.4)}`,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s',
                                }}
                            >
                                Select File
                            </Button>
                        </Box>
                    )}
                </Box>


                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        mt: 4,
                        pt: 3,
                        borderTop: `1px solid ${alpha('#dee2e6', 0.8)}`,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={uploading}
                        sx={{
                            borderRadius: '12px',
                            px: 3.5,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            color: '#6c757d',
                            borderColor: '#ced4da',
                            minWidth: '100px',
                            '&:hover': {
                                borderColor: safeButtonColor,
                                backgroundColor: alpha(safeButtonColor, 0.05),
                                color: safeButtonColor,
                            },
                            transition: 'all 0.2s',
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={(!captionFile && !existingCaptionUrl) || uploading}
                        onClick={handleUpload}
                        sx={{
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: '700',
                            fontSize: '0.95rem',
                            backgroundColor: safeButtonColor,
                            background: `linear-gradient(135deg, ${safeButtonColor} 0%, ${alpha(safeButtonColor, 0.8)} 100%)`,
                            boxShadow: `0 4px 12px ${alpha(safeButtonColor, 0.3)}`,
                            minWidth: '140px',
                            '&:hover': {
                                background: `linear-gradient(135deg, ${safeButtonColor} 0%, ${safeButtonColor} 100%)`,
                                boxShadow: `0 6px 20px ${alpha(safeButtonColor, 0.4)}`,
                                transform: 'translateY(-2px)',
                            },
                            '&:disabled': {
                                backgroundColor: '#cccccc',
                                background: '#cccccc',
                                boxShadow: 'none',
                                transform: 'none',
                            },
                            transition: 'all 0.3s',
                        }}
                    >
                        {uploading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={22} sx={{ color: 'white' }} />
                                <span>{captionFile ? 'Uploading...' : 'Deleting...'}</span>
                            </Box>
                        ) : existingCaptionUrl && captionFile ? (
                            'Replace Caption'
                        ) : (
                            'Upload Caption'
                        )}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default CaptionUploadModal;